import * as React from "react";
import PageHeaderView from "../../body/PageHeaderView";
import PageContentView from "../../body/PageContentView";
import {none, option, Option} from "ts-option";
import {connect} from "react-redux";
import {RootState} from "../../../store/reducer/root/RootState";
import Loading from "../../ui/loading/Loading";
import JobStatusesTable from "./JobStatusesTable";
import JobStatus from "../../../model/job/JobStatus";
import Toast from "../../helper/Toast";
import {OnFailure, OnSuccess} from "../../../store/action/actionHelpers";
import {updateJobStatusSortOrder} from "../../../store/action/jobStatus/jobStatus";
import {Dispatch} from "redux";
import CreateJobStatusModal from "../../jobStatus/modal/CreateJobStatusModal";
import EditJobStatusModal from "../../jobStatus/modal/EditJobStatusModal";
import ButtonGroupWrapper from "../../../hiberUiKit/src/atoms/Buttons/ButtonGroupWrapper";
import ButtonPrimary from "../../../hiberUiKit/src/atoms/Buttons/ButtonPrimary";

interface ReduxProps {
	/**
	 * All job statuses.
	 */
	jobStatuses: Option<JobStatus[]>;
	/**
	 * Sort order update method
	 * @param {JobStatus[]} items
	 * @param {OnSuccess} onSuccess
	 * @param {OnFailure} onFailure
	 */
	updateSortOrder: (items: JobStatus[], onSuccess: OnSuccess, onFailure: OnFailure) => void;
}

interface IComponentState {
	/**
	 * Job statuses with new sort order.
	 */
	jobStatuses: JobStatus[];
	/**
	 * Job status to edit
	 */
	editedJobStatus: Option<JobStatus>;
	/**
	 * Indicates if sort order has changed.
	 */
	isDirty: boolean;
	/**
	 * Job status create modal visibility flag
	 */
	createStatusModalVisible: boolean;
}

/**
 * Job status list component.
 */
class JobStatusesList extends React.Component<ReduxProps, IComponentState> {

	constructor(props: ReduxProps) {
		super(props);
		this.state = {
			jobStatuses: [],
			editedJobStatus: none,
			isDirty: false,
			createStatusModalVisible: false
		};
		this.onSubmitOrder = this.onSubmitOrder.bind(this);
		this.onOrderChange = this.onOrderChange.bind(this);
		this.renderCreateItemModal = this.renderCreateItemModal.bind(this);
		this.renderEditItemModal = this.renderEditItemModal.bind(this);
		this.openModal = this.openModal.bind(this);
		this.closeModal = this.closeModal.bind(this);
	}

	render() {
		return (
			<React.Fragment>
				<PageHeaderView header={"Job Statuses"}>
					<ButtonGroupWrapper>
						<ButtonPrimary onClick={this.openModal}
									   label={"Create Status"}
									   variant={"burgundy"}/>
						<ButtonPrimary onClick={() => this.onSubmitOrder(this.state.jobStatuses)}
									   disabled={this.props.jobStatuses.getOrElse([]).length === 0 || !this.state.isDirty}
									   label={"Save Changes"}
									   variant={"burgundy"}/>
					</ButtonGroupWrapper>
				</PageHeaderView>
				<PageContentView>
					<Loading isLoading={this.props.jobStatuses.isEmpty}>
						<JobStatusesTable jobStatuses={this.props.jobStatuses.getOrElse([])}
										  onRowClick={(jobStatus) => {this.setState({editedJobStatus: option(jobStatus)})}}
										  onOrderChange={this.onOrderChange}/>
					</Loading>
					{this.renderCreateItemModal()}
					{this.renderEditItemModal()}
				</PageContentView>
			</React.Fragment>
		);
	}

	/**
	 * Submit order change.
	 * @param {JobStatus[]} items
	 */
	onSubmitOrder(items: JobStatus[]) {
		this.props.updateSortOrder(
			items,
			() => {
				this.setState({isDirty: false},
					() => {
						Toast.info("Sort order has been updated.", "Update sort order");
					});
			},
			() => {
				Toast.error("Something went wrong. Please try again later.", "Update sort order")
			}
		);
	}

	/**
	 * Sort order change callback.
	 * @param {JobStatus[]} jobStatuses
	 */
	onOrderChange(jobStatuses: JobStatus[]) {
		this.setState({jobStatuses, isDirty: true});
	}

	/**
	 * Render create job status modal.
	 */
	renderCreateItemModal() {
		if (this.state.createStatusModalVisible) {
			return (
				<CreateJobStatusModal onSuccess={this.closeModal} onCancel={this.closeModal}/>
			);
		}
	}

	/**
	 * Close create job status modal.
	 */
	closeModal() {
		this.setState({createStatusModalVisible: false});
	}

	/**
	 * Open create job status modal.
	 */
	openModal() {
		this.setState({createStatusModalVisible: true});
	}

	/**
	 * Render modal.
	 */
	renderEditItemModal() {
		if (this.state.editedJobStatus.isDefined) {
			return (
				<EditJobStatusModal editedJobStatus={this.state.editedJobStatus.get}
									onSuccess={() => this.setState({editedJobStatus: none})}
									onCancel={() => this.setState({editedJobStatus: none})}/>
			);
		}
	}
}

function mapStateToProps(state: RootState) {
	const statuses = [].concat(state.jobStatus.jobStatuses.getOrElse([]));
	return {
		jobStatuses: state.jobStatus.jobStatuses.isDefined ? option(statuses) : none
	}
}

function mapDispatchToProps(dispatch: Dispatch<any>) {
	return {
		updateSortOrder: (items: JobStatus[], onSuccess: OnSuccess, onFailure: OnFailure) => dispatch(
			updateJobStatusSortOrder(items, onSuccess, onFailure)
		)
	}
}

export default connect(mapStateToProps, mapDispatchToProps)(JobStatusesList)
